import KindFilter from '@/components/KindFilter'
import NoteList, { TNoteListRef } from '@/components/NoteList'
import Tabs from '@/components/Tabs'
import RelaySelector, { Item } from '@/components/RelaySelector'

import { isTouchDevice } from '@/lib/utils'
import { loadRelayList, loadFavoriteRelays } from '@nostr/gadgets/lists'
import { useCurrentPubkey } from '@/atoms/session'
import { loadRelayInfo } from '@nostr/gadgets/relays'
import { TFeedSubRequest } from '@/types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { unwrappedPinListAtom, unwrappedFavoriteRelaysWithSetsAtom } from '@/atoms/lists'
import { RefreshButton } from '../RefreshButton'
import { Button } from '@/components/ui/button'
import { HugeiconsIcon } from '@hugeicons/react'
import { Router } from '@hugeicons/core-free-icons'
import { normalizeUrl } from '@/lib/url'
import { ready, sync } from '@/services/outbox.service'
import { loadPins } from '@nostr/gadgets/lists'
import { includeRepliesAtom } from '@/atoms/feed'
import { defaultShowKindsAtom } from '@/atoms/preferences'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'

const TABS_THRESHOLD = 20

export default function ProfileFeed({
  pubkey,
  topSpace = 0,
  search = ''
}: {
  pubkey: string
  topSpace?: number
  search?: string
}) {
  const myPubkey = useCurrentPubkey()
  const myPinList = useAtomValue(unwrappedPinListAtom)
  const includeReplies = useAtomValue(includeRepliesAtom)
  const setIncludeReplies = useSetAtom(includeRepliesAtom)
  const defaultShowKinds = useAtomValue(defaultShowKindsAtom)
  const [temporaryShowKinds, setTemporaryShowKinds] = useState<number[]>(() => defaultShowKinds)
  const { t } = useTranslation()

  // when coming from the grouped notes view this will be maximum timestamp threshold
  const groupedSince = parseInt(new URLSearchParams(window.location.search).get('gs') || '0')

  const [onlyYou, setOnlyYou] = useState(false)
  const [selectedRelayUrls, setSelectedRelayUrls] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(`relaySel:${pubkey}`) || '[]')
    } catch {
      return []
    }
  })
  const [hasForceSet, setHasForceSet] = useState<boolean>(false)
  const [subRequests, setSubRequests] = useState<null | TFeedSubRequest[]>(null)
  const [pinnedEventIds, setPinnedEventIds] = useState<string[]>([])

  // threshold for showing tabs - only show tabs in grouped notes view if total items > 20
  const [displayTabs, setDisplayTabs] = useState(groupedSince > 0)

  const supportTouch = useMemo(() => isTouchDevice(), [])
  const noteListRef = useRef<TNoteListRef>(null)

  const tabs = useMemo(
    () => [
      { value: 'posts', label: 'Notes' },
      { value: 'postsAndReplies', label: 'All' },
      ...(myPubkey && myPubkey !== pubkey ? [{ value: 'you', label: t('You') }] : [])
    ],
    [myPubkey, pubkey, t]
  )

  useEffect(() => {
    if (pubkey === myPubkey && myPinList) {
      setPinnedEventIds(myPinList)
    }
  }, [pubkey, myPubkey, myPinList])

  useAsync(async () => {
    if (pubkey === myPubkey) return
    const pinList = (await loadPins(pubkey)).items
    setPinnedEventIds(pinList)
  }, [pubkey, myPubkey])

  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)

  const { value: relayItems } = useAsync(async (): Promise<Item[]> => {
    const items: Item[] = []
    const addedUrls = new Set<string>()

    const rl = await loadRelayList(pubkey)
    const profileRelays = rl.items
    if (profileRelays.length > 0) {
      for (const r of profileRelays) {
        items.push({ kind: 'relay', url: r.url })
        addedUrls.add(normalizeUrl(r.url))
      }
      items.push({ kind: 'separator' })
    }

    for (const fav of favorites ?? []) {
      if (typeof fav === 'string') {
        if (!addedUrls.has(normalizeUrl(fav))) {
          items.push({ kind: 'relay', url: fav })
          addedUrls.add(normalizeUrl(fav))
        }
      } else {
        items.push({
          kind: 'relay-set',
          identifier: fav.pointer.identifier,
          relays: fav.items,
          name: fav.title
        })
      }
    }

    try {
      const pfav = await loadFavoriteRelays(pubkey)
      const relayUrls = pfav.items.filter((i): i is string => typeof i === 'string')
      if (relayUrls.length > 0) {
        items.push({ kind: 'separator' })
        for (const url of relayUrls) {
          if (!addedUrls.has(normalizeUrl(url))) {
            items.push({ kind: 'relay', url })
          }
        }
      }
    } catch (e) {
      console.warn('failed to load profile favorite relays', e)
    }

    return items
  }, [pubkey, favorites])

  useEffect(() => {
    const abort = new AbortController()

    ready()
      .then(() =>
        sync([pubkey], window.hallway.universe.feedKinds, {
          signal: abort.signal
        })
      )
      .catch((err) => {
        console.warn(`bailing on single-profile sync: ${err}`)
      })

    return () => {
      abort.abort('<cancelled>')
    }
  }, [pubkey])

  useEffect(() => {
    ;(async () => {
      if (onlyYou) {
        if (!myPubkey) {
          setSubRequests(null)
          return
        }

        setSubRequests([
          {
            source: 'local',
            filter: {
              authors: [myPubkey],
              '#p': [pubkey]
            }
          },
          {
            source: 'local',
            filter: {
              authors: [pubkey],
              '#p': [myPubkey]
            }
          }
        ])
        return
      }

      if (selectedRelayUrls.length > 0) {
        setSubRequests([
          {
            source: 'relays',
            urls: selectedRelayUrls,
            filter: { authors: [pubkey] }
          }
        ])
        return
      }

      if (myPubkey === pubkey) return
      const { items } = await loadRelayList(pubkey)

      if (search) {
        const writeRelays = items
          .filter((r) => r.write)
          .map((r) => r.url)
          .slice(0, 4)
        const relayInfos = await Promise.all(writeRelays.map((url) => loadRelayInfo(url)))
        const searchableRelays = writeRelays.filter((_, index) =>
          relayInfos[index]?.supported_nips?.includes?.(50)
        )
        setSubRequests([
          {
            source: 'relays',
            urls: searchableRelays.concat(window.hallway.universe.noteSearchRelayUrls).slice(0, 8),
            filter: { authors: [pubkey], search }
          }
        ])
        return
      }
    })()
  }, [pubkey, onlyYou, search, selectedRelayUrls])

  useAsync(async () => {
    if (search || onlyYou || selectedRelayUrls.length > 0) return

    setSubRequests([
      {
        source: 'local',
        filter: {
          authors: [pubkey]
        }
      }
    ])
  }, [pubkey, search, onlyYou, selectedRelayUrls])

  return (
    <>
      <Tabs
        value={onlyYou ? 'you' : includeReplies ? 'postsAndReplies' : 'posts'}
        tabs={tabs}
        onTabChange={(tabName) => {
          switch (tabName) {
            case 'you':
              setOnlyYou(true)
              setIncludeReplies(true)
              break
            case 'postsAndReplies':
              setIncludeReplies(true)
              break
            case 'posts':
              setIncludeReplies(false)
              break
          }

          noteListRef.current?.scrollToTop('smooth')
        }}
        threshold={Math.max(800, topSpace)}
        hideTabs={!displayTabs}
        options={
          <>
            {!supportTouch && <RefreshButton onClick={() => noteListRef.current?.refresh()} />}
            {relayItems && (
              <RelaySelector
                items={relayItems}
                selected={new Set(selectedRelayUrls)}
                onChange={(sel) => {
                  const urls = sel
                    .filter((s): s is { kind: 'relay'; url: string } => s.kind === 'relay')
                    .map((s) => s.url)
                  setSelectedRelayUrls(urls)
                  sessionStorage.setItem(`relaySel:${pubkey}`, JSON.stringify(urls))
                }}
              >
                <Button
                  variant="ghost"
                  size="titlebar-icon"
                  className="gap-1 px-3 w-auto shrink focus:text-foreground text-muted-foreground"
                >
                  <HugeiconsIcon icon={Router} size={16} />
                  <span className="hidden lg:block">
                    {selectedRelayUrls.length > 0
                      ? t('Relays: {{count}}', { count: selectedRelayUrls.length })
                      : t('Relays')}
                  </span>
                </Button>
              </RelaySelector>
            )}
            <KindFilter
              showKinds={temporaryShowKinds}
              onShowKindsChange={(newShowKinds: number[]) => {
                setTemporaryShowKinds(newShowKinds)
                noteListRef.current?.scrollToTop('instant')
              }}
            />
          </>
        }
      />
      <NoteList
        ref={noteListRef}
        subRequests={subRequests}
        showKinds={temporaryShowKinds}
        filterMutedNotes={false}
        sinceTimestamp={groupedSince}
        onNotesLoaded={(count: number, hasPosts: boolean, hasReplies: boolean) => {
          const displayOnGrouped =
            groupedSince && includeReplies && count > TABS_THRESHOLD && hasPosts && hasReplies

          setDisplayTabs(displayOnGrouped || !groupedSince)

          if (displayOnGrouped && !hasForceSet) {
            setIncludeReplies(true)
            setHasForceSet(true)
          }
        }}
        pinnedEventIds={
          onlyYou || !!search || groupedSince || selectedRelayUrls.length > 0
            ? undefined
            : pinnedEventIds
        }
      />
    </>
  )
}
